"use strict";

import { useAlertStore } from "@/pinia";
import { useWalletStore } from "@/pinia";

/**
 * Wrapper function for fetch that handles 401 unauthorized responses and empty JSON responses
 *
 * @param {string} url - The URL to fetch from
 * @param {Object} options - Fetch options object
 * @param {Function} [options.fetch] - Optional fetch implementation to use instead of global fetch
 * @param {string} [options.method] - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {Object} [options.headers] - HTTP headers
 * @param {string|Object} [options.body] - Request body
 * @param {AbortSignal} [options.signal] - AbortSignal to abort request
 * @returns {Promise<Response>} The fetch response with enhanced JSON handling
 *
 * Key features:
 * - Handles 401 unauthorized responses by:
 *   - Showing session expired notification
 *   - Clearing auth tokens and wallet state
 *   - Redirecting to login page
 * - Safely handles empty JSON responses by returning empty object instead of throwing
 * - Preserves original fetch functionality while adding error handling
 */

// @ts-ignore
async function fetchWithAuth(url, options = {}) {
	try {
		// Use the provided fetch function or the global fetch
		const fetchFn = options.fetch || fetch;
    const response = await fetchFn(url, options);

		if (response?.status === 401) {
			const alertStore = useAlertStore();
			const walletStore = useWalletStore();

			alertStore.error("Session Expired", "Please login again");

			localStorage.removeItem("fluxbot:auth");
			localStorage.removeItem("walletName");

			walletStore.clearWallet();

			window.location.href = "/auth/login";


			// Return empty response with 401 status to prevent further processing
			return new Response(null, { status: 401 });
		}

    // Store the original json method
    const originalJson = response.json;

    // Override the json method with a safe version that won't throw on empty responses
    // Sometime backend returns empty response, so we need to handle it, cuz our old code have lots of places parse json without checking if it's empty
    // This func will called when we call response.json() -> it's not affect other methods like response.text()
    response.json = async function() {
      try {
        // Try the original json method
        return await originalJson.call(this);
      } catch (error) {
        // If parsing fails (empty body or invalid JSON), return empty object
        console.warn(`JSON parsing failed for ${url}:`, error instanceof Error ? error.message : String(error));
        return {};  // Return empty object instead of throwing
      }
    };

		return response;
	} catch (error) {
    console.error('Network request failed:', url, error instanceof Error ? error.message : String(error));

    return new Response(JSON.stringify({ error: error instanceof Error ? error.message : String(error) }), {
      headers: { 'Content-Type': 'application/json' }
    });
	}
}

// Export the wrapper function
export default fetchWithAuth;